import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/App";
import usePageTitle from "@/hooks/usePageTitle";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  CircleDollarSign,
  Download,
  Printer,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  RefreshCw,
  Building2,
  Calendar
} from "lucide-react";

const OutstandingBalanceReportPage = () => {
  usePageTitle("Outstanding Balance Report");
  const { token } = useAuth();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: 'outstanding', direction: 'desc' });

  // Get date range from URL params
  const urlParams = new URLSearchParams(window.location.search);
  const dateFrom = urlParams.get('from');
  const dateTo = urlParams.get('to');

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/outstanding-balance${params.toString() ? `?${params}` : ''}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        toast.error("Failed to fetch report");
      }
    } catch (error) {
      toast.error("Error loading report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [token, dateFrom, dateTo]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-emerald-600" />
      : <ArrowDown className="w-3 h-3 ml-1 text-emerald-600" />;
  };

  const filteredAndSortedPartners = useMemo(() => {
    if (!reportData?.partners) return [];
    
    let filtered = reportData.partners;
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.client_code?.toLowerCase().includes(term) ||
        p.client_name?.toLowerCase().includes(term)
      );
    }
    
    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      const aVal = a[sortConfig.key] || 0;
      const bVal = b[sortConfig.key] || 0;
      
      if (sortConfig.key === 'client_code' || sortConfig.key === 'client_name') {
        return sortConfig.direction === 'asc' 
          ? (aVal || '').localeCompare(bVal || '')
          : (bVal || '').localeCompare(aVal || '');
      }
      
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    return sorted;
  }, [reportData, searchTerm, sortConfig]);

  const exportToCSV = () => {
    if (!reportData?.partners) return;
    
    const headers = ["Partner Code", "Partner Name", "Total Billing", "Medicine Cost", "Shipping Cost", "Total Paid", "Outstanding", "Parcels"];
    const rows = filteredAndSortedPartners.map(p => [
      p.client_code,
      p.client_name,
      p.total_costing.toFixed(2),
      p.medicine_cost.toFixed(2),
      p.shipping_cost.toFixed(2),
      p.total_paid.toFixed(2),
      p.outstanding.toFixed(2),
      p.parcel_count
    ]);
    
    // Add grand total
    rows.push([
      "GRAND TOTAL",
      "",
      reportData.grand_total.total_costing.toFixed(2),
      reportData.grand_total.medicine_cost.toFixed(2),
      reportData.grand_total.shipping_cost.toFixed(2),
      reportData.grand_total.total_paid.toFixed(2),
      reportData.grand_total.outstanding.toFixed(2),
      reportData.grand_total.parcel_count
    ]);
    
    const csvContent = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `outstanding_balance_report_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported!");
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6 print:p-2">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
          .print-break { page-break-before: always; }
          table { font-size: 10px; }
          th, td { padding: 4px 6px !important; }
        }
      `}</style>

      {/* Header - Non-printable controls */}
      <div className="no-print sticky top-0 z-20 bg-white dark:bg-gray-800 border-b shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <CircleDollarSign className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Outstanding Balance Report</h1>
                <p className="text-sm text-muted-foreground">
                  Generated: {reportData?.generated_at ? format(new Date(reportData.generated_at), 'dd MMM yyyy, HH:mm') : '-'}
                  {(dateFrom || dateTo) && (
                    <span className="ml-2">
                      | Period: {dateFrom || 'Start'} to {dateTo || 'Today'}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={fetchReport} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={exportToCSV}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button size="sm" onClick={handlePrint} className="bg-emerald-600 hover:bg-emerald-700">
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Printable Content */}
      <div className="max-w-[1600px] mx-auto p-6 print:p-2 print:max-w-none">
        {/* Print Header - Only visible when printing */}
        <div className="hidden print:block mb-4">
          <h1 className="text-2xl font-bold text-center">Outstanding Balance Report</h1>
          <p className="text-center text-gray-600">
            Generated: {reportData?.generated_at ? format(new Date(reportData.generated_at), 'dd MMM yyyy, HH:mm') : '-'}
            {(dateFrom || dateTo) && ` | Period: ${dateFrom || 'Start'} to ${dateTo || 'Today'}`}
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/30 dark:to-green-950/30 border-emerald-200">
            <CardContent className="p-4">
              <p className="text-xs font-medium text-emerald-600 uppercase">Outstanding Balance</p>
              <p className="text-2xl font-bold text-emerald-700 mt-1">
                ₹{(reportData?.grand_total?.outstanding || 0).toLocaleString()}
              </p>
              <p className="text-xs text-emerald-500">{reportData?.partner_count || 0} partners</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs font-medium text-muted-foreground uppercase">Total Billing</p>
              <p className="text-2xl font-bold mt-1">
                ₹{(reportData?.grand_total?.total_costing || 0).toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground">{reportData?.grand_total?.parcel_count || 0} parcels</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 border-green-200">
            <CardContent className="p-4">
              <p className="text-xs font-medium text-green-600 uppercase">Total Paid</p>
              <p className="text-2xl font-bold text-green-700 mt-1">
                ₹{(reportData?.grand_total?.total_paid || 0).toLocaleString()}
              </p>
              <p className="text-xs text-green-500">Collected</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs font-medium text-muted-foreground uppercase">Collection Rate</p>
              <p className="text-2xl font-bold mt-1">
                {reportData?.grand_total?.total_costing > 0 
                  ? Math.round((reportData.grand_total.total_paid / reportData.grand_total.total_costing) * 100) 
                  : 0}%
              </p>
              <p className="text-xs text-muted-foreground">Of total billing</p>
            </CardContent>
          </Card>
        </div>

        {/* Search - Non-printable */}
        <div className="no-print mb-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by partner code or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Main Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-emerald-50 dark:bg-emerald-950/30">
                    <TableHead className="w-12 font-semibold">#</TableHead>
                    <TableHead 
                      className="font-semibold cursor-pointer hover:bg-emerald-100 dark:hover:bg-emerald-900/50"
                      onClick={() => handleSort('client_code')}
                    >
                      <div className="flex items-center">
                        Partner Code
                        <SortIcon columnKey="client_code" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="font-semibold cursor-pointer hover:bg-emerald-100"
                      onClick={() => handleSort('client_name')}
                    >
                      <div className="flex items-center">
                        Partner Name
                        <SortIcon columnKey="client_name" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-right cursor-pointer hover:bg-emerald-100"
                      onClick={() => handleSort('total_costing')}
                    >
                      <div className="flex items-center justify-end">
                        Total Billing
                        <SortIcon columnKey="total_costing" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-right cursor-pointer hover:bg-emerald-100"
                      onClick={() => handleSort('total_paid')}
                    >
                      <div className="flex items-center justify-end">
                        Total Paid
                        <SortIcon columnKey="total_paid" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-right cursor-pointer hover:bg-emerald-100"
                      onClick={() => handleSort('outstanding')}
                    >
                      <div className="flex items-center justify-end">
                        Outstanding
                        <SortIcon columnKey="outstanding" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-right cursor-pointer hover:bg-emerald-100"
                      onClick={() => handleSort('parcel_count')}
                    >
                      <div className="flex items-center justify-end">
                        Parcels
                        <SortIcon columnKey="parcel_count" />
                      </div>
                    </TableHead>
                    <TableHead className="font-semibold text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAndSortedPartners.map((partner, idx) => (
                    <TableRow 
                      key={partner.client_code} 
                      className={`hover:bg-gray-50 dark:hover:bg-gray-800 ${partner.outstanding > 0 ? '' : 'bg-green-50/50 dark:bg-green-950/20'}`}
                    >
                      <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                      <TableCell className="font-mono font-medium">{partner.client_code}</TableCell>
                      <TableCell>{partner.client_name}</TableCell>
                      <TableCell className="text-right font-medium">
                        ₹{partner.total_costing.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right text-green-600 dark:text-green-400">
                        ₹{partner.total_paid.toLocaleString()}
                      </TableCell>
                      <TableCell className={`text-right font-bold ${partner.outstanding > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                        ₹{partner.outstanding.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">{partner.parcel_count}</TableCell>
                      <TableCell className="text-center">
                        {partner.outstanding > 0 ? (
                          <Badge variant="destructive" className="text-xs">Due</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">Paid</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  
                  {/* Grand Total Row */}
                  <TableRow className="bg-emerald-100 dark:bg-emerald-900/50 font-bold border-t-2">
                    <TableCell colSpan={3} className="text-right">
                      GRAND TOTAL ({filteredAndSortedPartners.length} partners)
                    </TableCell>
                    <TableCell className="text-right">
                      ₹{(reportData?.grand_total?.total_costing || 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right text-green-600">
                      ₹{(reportData?.grand_total?.total_paid || 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right text-red-600">
                      ₹{(reportData?.grand_total?.outstanding || 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {reportData?.grand_total?.parcel_count || 0}
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Print Footer */}
        <div className="hidden print:block mt-4 pt-4 border-t text-center text-xs text-gray-500">
          <p>EAC - Outstanding Balance Report | Page 1</p>
        </div>
      </div>
    </div>
  );
};

export default OutstandingBalanceReportPage;
